import { useId, useState } from 'react';

/**
 * Layered area chart: one translucent band per Page, one chart per measure.
 *
 * Three things make it read the way it does.
 *
 * **Smooth, not jagged.** The curve is monotone cubic (Fritsch–Carlson), not a
 * polyline and not a plain spline. A plain spline overshoots between points and
 * would draw a dip below zero on the way into a quiet day — inventing negative
 * comments. Monotone interpolation cannot overshoot, so the curve is soft and
 * still truthful.
 *
 * **Layered from the baseline, not stacked.** Each band starts at zero and is
 * drawn over the others at partial opacity, so a band's height is its own value
 * rather than a sum you have to subtract to read. The cost of that choice is
 * occlusion — a small band can hide behind a large one — which is why the draw
 * order is by total, descending: the tallest goes down first and the smallest
 * ends up in front. Nothing is ever completely covered.
 *
 * **The palette is fixed and validated, not chosen.** It comes from the
 * reference instance in the dataviz skill and passes all six checks in both
 * light and dark. The product's own accent hues were tried first and failed:
 * one read as grey, and two adjacent slots were 4.4 apart under deuteranopia
 * where the floor is 8.
 *
 * A colour belongs to a Page, and the mapping is decided once for all three
 * charts. Otherwise switching measure would repaint the bands and quietly
 * rename them.
 */

/* Eight slots, assigned in order and never cycled. A ninth series folds into
   "other" upstream rather than inventing a hue that would fail the checks. */
const LIGHT = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
  '#4a3aa7',
  '#e34948',
];
const DARK = [
  '#3987e5',
  '#d95926',
  '#199e70',
  '#c98500',
  '#d55181',
  '#008300',
  '#9085e9',
  '#e66767',
];

export interface Band {
  pageId: string;
  name: string;
}

/**
 * Monotone cubic path through the points.
 *
 * Tangents are damped wherever the data changes direction, which is what stops
 * the curve bulging past the values it connects.
 */
function smoothPath(points: Array<{ x: number; y: number }>): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M${points[0]!.x},${points[0]!.y}`;

  const n = points.length;
  const slopes: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    const dx = points[i + 1]!.x - points[i]!.x;
    slopes.push(dx === 0 ? 0 : (points[i + 1]!.y - points[i]!.y) / dx);
  }

  const tangents: number[] = new Array(n).fill(0);
  tangents[0] = slopes[0] ?? 0;
  tangents[n - 1] = slopes[n - 2] ?? 0;
  for (let i = 1; i < n - 1; i += 1) {
    const before = slopes[i - 1]!;
    const after = slopes[i]!;
    // A turning point gets a flat tangent: that is the whole trick.
    tangents[i] = before * after <= 0 ? 0 : (before + after) / 2;
  }
  for (let i = 0; i < n - 1; i += 1) {
    if (slopes[i] === 0) {
      tangents[i] = 0;
      tangents[i + 1] = 0;
    }
  }

  let path = `M${points[0]!.x},${points[0]!.y}`;
  for (let i = 0; i < n - 1; i += 1) {
    const dx = points[i + 1]!.x - points[i]!.x;
    const c1x = points[i]!.x + dx / 3;
    const c1y = points[i]!.y + (tangents[i]! * dx) / 3;
    const c2x = points[i + 1]!.x - dx / 3;
    const c2y = points[i + 1]!.y - (tangents[i + 1]! * dx) / 3;
    path += ` C${c1x},${c1y} ${c2x},${c2y} ${points[i + 1]!.x},${points[i + 1]!.y}`;
  }
  return path;
}

export function StackedArea({
  title,
  bands,
  days,
  values,
  locale,
  emptyLabel,
}: {
  title: string;
  bands: Band[];
  days: string[];
  /** One array per day, one entry per band, already in band order. */
  values: number[][];
  locale: string;
  emptyLabel: string;
}) {
  const gradientPrefix = useId().replace(/:/g, '');
  const [hover, setHover] = useState<number | null>(null);

  const width = 100;
  const height = 36;

  const bandTotals = bands.map((_, index) =>
    values.reduce((sum, day) => sum + (day[index] ?? 0), 0),
  );
  const peak = Math.max(1, ...values.flatMap((day) => day));
  const grandTotal = bandTotals.reduce((sum, value) => sum + value, 0);

  if (grandTotal === 0) {
    return (
      <figure className="area-chart">
        <figcaption>{title}</figcaption>
        <p className="muted small area-empty">{emptyLabel}</p>
      </figure>
    );
  }

  const x = (index: number) =>
    days.length === 1 ? width / 2 : (index / (days.length - 1)) * width;

  /*
   * Tallest first so it is painted furthest back. Layering from the baseline
   * means bands overlap; without this, a busy Page would bury a quiet one.
   */
  const order = bands
    .map((_, index) => index)
    .sort((a, b) => (bandTotals[b] ?? 0) - (bandTotals[a] ?? 0));

  const areaPath = (bandIndex: number): string => {
    const points = values.map((day, dayIndex) => ({
      x: x(dayIndex),
      y: height - ((day[bandIndex] ?? 0) / peak) * height,
    }));
    return `${smoothPath(points)} L${x(values.length - 1)},${height} L0,${height} Z`;
  };

  const readable = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
    });

  return (
    <figure className="area-chart">
      <figcaption>{title}</figcaption>

      {/* The totals double as the key: each number carries its band's colour on
          a rule beneath it, so identity is never carried by the fill alone. */}
      <div className="area-heads">
        {bands.map((band, index) => (
          <div className="area-head" key={band.pageId}>
            <strong>{bandTotals[index]}</strong>
            <span className="area-rule" style={{ background: `var(--series-${index + 1})` }} />
            <span className="area-head-name">{band.name}</span>
          </div>
        ))}
      </div>

      <div className="area-plot">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={title}
        >
          <defs>
            {bands.map((band, index) => (
              <linearGradient
                key={band.pageId}
                id={`${gradientPrefix}-${index}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={`var(--series-${index + 1})`} stopOpacity="0.78" />
                <stop offset="100%" stopColor={`var(--series-${index + 1})`} stopOpacity="0.12" />
              </linearGradient>
            ))}
          </defs>

          {order.map((bandIndex) => (
            <path
              key={bands[bandIndex]!.pageId}
              d={areaPath(bandIndex)}
              fill={`url(#${gradientPrefix}-${bandIndex})`}
              stroke={`var(--series-${bandIndex + 1})`}
              strokeWidth="1.5"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {hover !== null && (
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1="0"
              y2={height}
              stroke="var(--text-faint)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {/* Hit targets wider than the marks: a one-pixel slice of curve is not
            something anyone can point at, least of all with a thumb. */}
        <div className="area-hits">
          {days.map((day, index) => (
            <button
              key={day}
              type="button"
              aria-label={`${readable(day)}: ${values[index]?.reduce((a, b) => a + b, 0) ?? 0}`}
              onMouseEnter={() => setHover(index)}
              onFocus={() => setHover(index)}
              onMouseLeave={() => setHover(null)}
              onBlur={() => setHover(null)}
            />
          ))}
        </div>

        {hover !== null && (
          <div
            className="area-tip"
            style={{
              insetInlineStart: `${(hover / Math.max(1, days.length - 1)) * 100}%`,
            }}
          >
            <strong>{readable(days[hover] as string)}</strong>
            {bands.map((band, index) => {
              const value = values[hover]?.[index] ?? 0;
              if (value === 0) return null;
              return (
                <span key={band.pageId}>
                  <i style={{ background: `var(--series-${index + 1})` }} />
                  {band.name}
                  <b>{value}</b>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="area-axis">
        {days.map((day, index) => {
          // Thinned so the labels never collide; the curve keeps every day.
          const step = Math.max(1, Math.ceil(days.length / 8));
          if (index % step !== 0 && index !== days.length - 1) return null;
          return (
            <span
              key={day}
              style={{
                insetInlineStart: `${(index / Math.max(1, days.length - 1)) * 100}%`,
              }}
            >
              {new Date(day).toLocaleDateString(locale, { day: '2-digit' })}
            </span>
          );
        })}
      </div>
    </figure>
  );
}

/** The palette as CSS variables, so the SVG can name a slot rather than a hex. */
export function seriesPaletteStyle(dark: boolean): Record<string, string> {
  const palette = dark ? DARK : LIGHT;
  return Object.fromEntries(palette.map((hex, index) => [`--series-${index + 1}`, hex]));
}
